/**
 * Duration arithmetic for maintenance barriers.
 *
 * Barrier times are stored as `YYYY-MM-DD HH:MM` in Zulu, so every value is
 * normalized to an explicit UTC instant before parsing rather than relying on
 * the browser's local-time interpretation.
 */
import type { MaintenanceBarrier } from './BarriersSection';

const MILLISECONDS_PER_HOUR = 1000 * 60 * 60;

/**
 * Parses a barrier datetime string (`YYYY-MM-DD HH:MM`) into epoch milliseconds.
 * Returns null when the value is missing or unparseable.
 */
function parseBarrierTime(value: string): number | null {
  if (!value) return null;
  const parsed = new Date(value.replace(' ', 'T') + ':00Z');
  return isNaN(parsed.getTime()) ? null : parsed.getTime();
}

/**
 * Calculates barrier duration in hours to two decimal places. An open barrier
 * (no end time) accrues up to now. Returns '—' when the times can't be read and
 * '0.00' when the range is inverted.
 */
export function calculateDuration(barrier: MaintenanceBarrier): string {
  const start = parseBarrierTime(barrier.startTime);
  if (start === null) return '—';

  let end: number;
  if (barrier.endTime) {
    const parsedEnd = parseBarrierTime(barrier.endTime);
    if (parsedEnd === null) return '—';
    end = parsedEnd;
  } else {
    end = Date.now();
  }

  const diffMs = end - start;
  if (diffMs < 0) return '0.00';
  return (diffMs / MILLISECONDS_PER_HOUR).toFixed(2);
}

/**
 * Sums the total open hours per barrier type across all CLOSED barriers (those
 * with both a valid start and end time). Open barriers are excluded so a type
 * total only appears once at least one barrier of that type closes.
 */
export function calculateClosedTotalsByType(
  barriers: MaintenanceBarrier[],
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const barrier of barriers) {
    if (!barrier.endTime) continue;
    const start = parseBarrierTime(barrier.startTime);
    const end = parseBarrierTime(barrier.endTime);
    if (start === null || end === null) continue;
    const diffMs = end - start;
    if (diffMs <= 0) continue;
    totals[barrier.type] = (totals[barrier.type] || 0) + diffMs / MILLISECONDS_PER_HOUR;
  }
  return totals;
}
